import { useEffect } from 'react';

export interface ActiveObjectDetail {
  /** Object kind the agent reasons about: 'email' | 'event' | 'task' | 'contact'
   *  | 'email-stats' | … For a data VIEW (not a single object), use a view kind
   *  (e.g. 'email-stats'), an empty `objectId`, and a compact `summary`. */
  objectType: string;
  /** Stable id the agent acts on (for email this is the threadId). '' for a view. */
  objectId: string;
  /** Human-meaningful label (subject / title / name / screen name). */
  name: string;
  /** Optional address (e.g. a contact's email). */
  email?: string;
  /** Optional compact, on-screen data summary for VIEW surfaces (e.g. Email
   *  Stats) — the agent answers "what am I looking at" from this instead of
   *  recomputing via tools. Keep it token-bounded (a few lines). */
  summary?: string;
}

/**
 * W3 screen-as-scope — broadcast the currently-open object to the chat over the
 * `xavior:active-object-changed` bus the chat listens on (ChatBox.tsx). The agent
 * uses it to resolve "this" / "reply to this" / "move it" to the open object
 * WITHOUT searching (see prompt-assembler `activeObjectContext`).
 *
 * Pass the open object's detail, or `null` when nothing is open. Re-broadcasts
 * only when the object's identity/label changes, and clears (null) on unmount so
 * chat never thinks a stale object is open after the module closes.
 *
 * Shared by the email / calendar / tasks modules (mirrors the inline dispatch the
 * Contacts module still uses).
 */
export function useActiveObjectBroadcast(detail: ActiveObjectDetail | null): void {
  // Key on the serialized detail so the broadcast effect fires on identity/label
  // change, not on every render (a fresh object literal each render would).
  const json = detail ? JSON.stringify(detail) : '';
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('xavior:active-object-changed', { detail: json ? JSON.parse(json) : null }),
    );
  }, [json]);
  useEffect(
    () => () => {
      window.dispatchEvent(new CustomEvent('xavior:active-object-changed', { detail: null }));
    },
    [],
  );
}
